import { useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Slider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Category as CategoryIcon,
    Close as CloseIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    ToggleOff as ToggleOffIcon,
    ToggleOn as ToggleOnIcon,
} from '@mui/icons-material';
import {
    useAddCategoryMutation,
    useDeleteCategoryMutation,
    useGetAdminDataQuery,
    useGetCategoriesResourcesQuery,
    useUpdateCategoryMutation,
} from '@/store/apiSlice';
import { useCategoriesResources } from '@/store/hooks';
import type { Category } from '@/types';
import { useI18nContext } from '@/i18n/i18n-react';
import CatIcon, { CATEGORY_ICONS } from '@/components/CategoryIcon.tsx';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader.tsx';
import { RefetchButton } from '@/components/admin/RefetchButton.tsx';
import { useAppDialog } from '@/components/dialogs/AppDialog';
import { getApiErrorMessage } from '@/utils/apiError';
import {
    compareBoolean,
    compareBy,
    compareNumber,
    compareString,
    invert,
    SortableHeaderCell,
    SortDirection,
    stableSort,
} from '@/components/admin/SortableTable';

interface CategoryFormData {
    id: number;
    name: string;
    icon: string;
    group: string;
    active?: number;
    setupTime?: number;
    teardownTime?: number;
    sortPriority?: number;
    allowMultiple?: number;
    minAssignments?: number;
    maxAssignments?: number;
    notifyDaysBeforeWhenUnassigned?: number | null;
}

const AdminCategoriesPage = () => {
    const { LL } = useI18nContext();
    const { alert, confirm } = useAppDialog();
    const [searchQuery, setSearchQuery] = useState('');
    const [editingCategory, setEditingCategory] = useState<CategoryFormData | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sortKey, setSortKey] = useState<string>('group');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const categories = useCategoriesResources();
    const { isLoading: isLoadingCategoriesResources, refetch: refetchCategoriesResources } =
        useGetCategoriesResourcesQuery();
    const { isLoading: isLoadingAdminData, refetch: refetchAdminData } = useGetAdminDataQuery();
    const [addCategory] = useAddCategoryMutation();
    const [updateCategory] = useUpdateCategoryMutation();
    const [deleteCategory] = useDeleteCategoryMutation();

    const handleRefetch = () => {
        refetchCategoriesResources();
        refetchAdminData();
    };

    // Get unique groups from existing categories
    const uniqueGroups = useMemo(() => {
        if (!categories) return [];
        const groups = new Set(categories.map((cat) => cat.group));
        return Array.from(groups).sort();
    }, [categories]);

    // Calculate assignments count per category (unique resources assigned)
    const assignmentsCountByCategoryId = useMemo(() => {
        const counts: Record<number, number> = {};

        (categories ?? []).forEach((cat) => {
            const resources = cat.resources ?? [];
            const uniqueResourceIds = new Set<number>();
            resources.forEach((r: any) => {
                if (typeof r?.id === 'number') uniqueResourceIds.add(r.id);
            });
            counts[cat.id] = uniqueResourceIds.size;
        });

        return counts;
    }, [categories]);

    // Flattened table rows (keep group as column instead of grouping cards)
    const tableRows = useMemo(() => {
        if (!categories) {
            return [];
        }

        let filtered = [...categories];

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(
                (cat) =>
                    cat.name.toLowerCase().includes(query) ||
                    cat.group.toLowerCase().includes(query) ||
                    cat.icon.toLowerCase().includes(query)
            );
        }

        const getAssignments = (categoryId: number) =>
            assignmentsCountByCategoryId[categoryId] ?? 0;

        const comparator = (() => {
            switch (sortKey) {
                case 'name':
                    return compareBy((c: Category) => c.name ?? '', compareString);
                case 'group':
                    return compareBy((c: Category) => c.group ?? '', compareString);
                case 'active':
                    return compareBy((c: Category) => c.active === 1, compareBoolean);
                case 'assignments':
                    return compareBy((c: Category) => getAssignments(c.id), compareNumber);
                case 'allowMultiple':
                    return compareBy((c: Category) => Boolean(c.allowMultiple), compareBoolean);
                case 'sortPriority':
                    return compareBy((c: Category) => c.sortPriority ?? 500, compareNumber);
                case 'setupTime':
                    return compareBy((c: Category) => c.setupTime ?? 30, compareNumber);
                case 'teardownTime':
                    return compareBy((c: Category) => c.teardownTime ?? 15, compareNumber);
                default:
                    return compareBy((c: Category) => c.group ?? '', compareString);
            }
        })();

        return stableSort(filtered, sortDirection === 'asc' ? comparator : invert(comparator));
    }, [categories, searchQuery, assignmentsCountByCategoryId, sortKey, sortDirection]);

    const handleSortChange = (key: string) => {
        if (sortKey !== key) {
            setSortKey(key);
            setSortDirection('asc');
            return;
        }
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    };

    const handleOpenModal = (category?: Category) => {
        if (category) {
            setEditingCategory({
                id: category.id,
                name: category.name,
                icon: category.icon,
                group: category.group,
                active: category.active,
                setupTime: category.setupTime ?? 30,
                teardownTime: category.teardownTime ?? 15,
                sortPriority: category.sortPriority ?? 500,
                allowMultiple: category.allowMultiple ? 1 : 0,
                minAssignments: category.minAssignments ?? 1,
                maxAssignments: category.maxAssignments ?? 1,
                notifyDaysBeforeWhenUnassigned: category.notifyDaysBeforeWhenUnassigned ?? null,
            });
        } else {
            setEditingCategory({
                id: 0,
                name: '',
                icon: '',
                group: '',
                active: 1,
                setupTime: 30,
                teardownTime: 15,
                sortPriority: 500,
                allowMultiple: 0,
                minAssignments: 1,
                maxAssignments: 1,
                notifyDaysBeforeWhenUnassigned: null,
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingCategory(null);
    };

    const handleSaveCategory = async () => {
        if (!editingCategory) {
            return;
        }

        try {
            if (editingCategory.id) {
                await updateCategory({
                    ...editingCategory,
                    allowMultiple: editingCategory.allowMultiple ?? 0,
                    minAssignments: editingCategory.minAssignments ?? 1,
                    maxAssignments: editingCategory.maxAssignments ?? 1,
                }).unwrap();
            } else {
                await addCategory(editingCategory).unwrap();
            }
            handleCloseModal();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleDeleteCategory = async (categoryId: number) => {
        if (!(await confirm(LL.adminCategoriesPage.deleteConfirm()))) {
            return;
        }

        try {
            await deleteCategory({ id: categoryId }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleToggleCategoryActivation = async (category: Category) => {
        const newActiveState = category.active === 1 ? 0 : 1;
        const actionText =
            newActiveState === 0
                ? LL.adminCategoriesPage.deactivateAction()
                : LL.adminCategoriesPage.activateAction();

        if (
            !(await confirm(
                LL.adminCategoriesPage.activateConfirm({
                    action: actionText,
                    name: category.name,
                })
            ))
        ) {
            return;
        }

        try {
            await updateCategory({
                id: category.id,
                name: category.name,
                icon: category.icon,
                group: category.group,
                active: newActiveState,
                setupTime: category.setupTime,
                teardownTime: category.teardownTime,
                sortPriority: category.sortPriority,
                allowMultiple: category.allowMultiple ? 1 : 0,
                minAssignments: category.minAssignments,
                maxAssignments: category.maxAssignments,
            }).unwrap();
        } catch (error) {
            await alert(
                LL.errors.errorUpdatingCategory({
                    message: getApiErrorMessage(error),
                })
            );
        }
    };

    if (isLoadingCategoriesResources) {
        return (
            <Stack
                sx={{
                    direction: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    gap: 2,
                }}
            >
                <CircularProgress size={60} />
                <Typography
                    variant="h6"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    {LL.common.loading()}
                </Typography>
            </Stack>
        );
    }

    return (
        <>
            <AdminPageHeader
                icon={<CategoryIcon />}
                title={LL.adminCategoriesPage.title()}
                subtitle={LL.adminCategoriesPage.subtitle()}
                beforeActions={
                    <TextField
                        size="small"
                        placeholder={LL.adminCategoriesPage.searchPlaceholder()}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ minWidth: 200 }}
                    />
                }
                refetchButton={
                    <RefetchButton
                        onRefetch={handleRefetch}
                        isRefetching={isLoadingCategoriesResources || isLoadingAdminData}
                    />
                }
                actions={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenModal()}
                    >
                        {LL.adminCategoriesPage.addCategory()}
                    </Button>
                }
            />
            <Box sx={{ px: 3 }}>
                {tableRows.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <SortableHeaderCell
                                        label={LL.categoryForm.categoryName()}
                                        sortKey="name"
                                        activeKey={sortKey}
                                        direction={sortDirection}
                                        onChange={handleSortChange}
                                    />
                                    <SortableHeaderCell
                                        label={LL.categoryForm.group()}
                                        sortKey="group"
                                        activeKey={sortKey}
                                        direction={sortDirection}
                                        onChange={handleSortChange}
                                    />
                                    <SortableHeaderCell
                                        label={LL.adminCategoriesPage.columns.status()}
                                        sortKey="active"
                                        activeKey={sortKey}
                                        direction={sortDirection}
                                        onChange={handleSortChange}
                                    />
                                    <SortableHeaderCell
                                        label={LL.adminCategoriesPage.columns.assignments()}
                                        sortKey="assignments"
                                        activeKey={sortKey}
                                        direction={sortDirection}
                                        onChange={handleSortChange}
                                    />
                                    <SortableHeaderCell
                                        label={LL.adminCategoriesPage.allowMultipleAssignments()}
                                        sortKey="allowMultiple"
                                        activeKey={sortKey}
                                        direction={sortDirection}
                                        onChange={handleSortChange}
                                    />
                                    <SortableHeaderCell
                                        label={LL.categoryForm.sortPriority()}
                                        sortKey="sortPriority"
                                        activeKey={sortKey}
                                        direction={sortDirection}
                                        onChange={handleSortChange}
                                    />
                                    <SortableHeaderCell
                                        label={LL.adminCategoriesPage.columns.setupTime()}
                                        sortKey="setupTime"
                                        activeKey={sortKey}
                                        direction={sortDirection}
                                        onChange={handleSortChange}
                                    />
                                    <SortableHeaderCell
                                        label={LL.adminCategoriesPage.columns.teardownTime()}
                                        sortKey="teardownTime"
                                        activeKey={sortKey}
                                        direction={sortDirection}
                                        onChange={handleSortChange}
                                    />
                                    <TableCell>{LL.adminCategoriesPage.notification()}</TableCell>
                                    <TableCell align="right">
                                        {LL.adminCategoriesPage.columns.actions()}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableRows.map((category) => {
                                    const assignments =
                                        assignmentsCountByCategoryId[category.id] ?? 0;
                                    return (
                                        <TableRow
                                            key={category.id}
                                            sx={{ opacity: category.active === 0 ? 0.6 : 1 }}
                                        >
                                            <TableCell>
                                                <Stack direction="row" spacing={1}>
                                                    <CatIcon category={category.icon} />
                                                    {category.name}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={category.group}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        category.active === 1
                                                            ? LL.common.active()
                                                            : LL.common.inactive()
                                                    }
                                                    size="small"
                                                    variant="filled"
                                                    color={
                                                        category.active === 1
                                                            ? 'success'
                                                            : 'default'
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={assignments}
                                                    size="small"
                                                    variant="filled"
                                                    color="default"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {category.allowMultiple ? (
                                                    <Chip
                                                        label={`${category.minAssignments ?? 1} - ${category.maxAssignments ?? 1}`}
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell>{category.sortPriority ?? 500}</TableCell>
                                            <TableCell>{category.setupTime ?? 30}</TableCell>
                                            <TableCell>{category.teardownTime ?? 15}</TableCell>
                                            <TableCell>
                                                {category.notifyDaysBeforeWhenUnassigned !== null &&
                                                category.notifyDaysBeforeWhenUnassigned !==
                                                    undefined ? (
                                                    <Chip
                                                        label={String(
                                                            category.notifyDaysBeforeWhenUnassigned
                                                        )}
                                                        size="small"
                                                        color="warning"
                                                    />
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                    sx={{ flexWrap: 'nowrap' }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        color={
                                                            category.active === 1
                                                                ? 'success'
                                                                : 'warning'
                                                        }
                                                        onClick={() =>
                                                            handleToggleCategoryActivation(category)
                                                        }
                                                        title={
                                                            category.active === 1
                                                                ? LL.adminCategoriesPage.deactivateTooltip()
                                                                : LL.adminCategoriesPage.activateTooltip()
                                                        }
                                                    >
                                                        {category.active === 1 ? (
                                                            <ToggleOnIcon fontSize="small" />
                                                        ) : (
                                                            <ToggleOffIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenModal(category)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() =>
                                                            handleDeleteCategory(category.id)
                                                        }
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            {LL.emptyState.noUsers()}
                        </Typography>
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                mb: 2,
                            }}
                        >
                            {searchQuery
                                ? LL.adminCategoriesPage.emptyStateSearch()
                                : LL.adminCategoriesPage.emptyStateNoCategories()}
                        </Typography>
                        {!searchQuery && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenModal()}
                            >
                                {LL.adminCategoriesPage.addCategory()}
                            </Button>
                        )}
                    </Paper>
                )}

                <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {editingCategory?.id
                            ? LL.adminCategoriesPage.editCategory()
                            : LL.adminCategoriesPage.addCategory()}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                label={LL.categoryForm.categoryName()}
                                fullWidth
                                value={editingCategory?.name || ''}
                                onChange={(e) =>
                                    setEditingCategory((prev) =>
                                        prev ? { ...prev, name: e.target.value } : null
                                    )
                                }
                                required
                            />
                            <Autocomplete
                                freeSolo
                                options={CATEGORY_ICONS}
                                value={editingCategory?.icon || ''}
                                onChange={(_, newValue) => {
                                    setEditingCategory((prev) =>
                                        prev ? { ...prev, icon: newValue || '' } : null
                                    );
                                }}
                                onInputChange={(_, newInputValue) => {
                                    setEditingCategory((prev) =>
                                        prev ? { ...prev, icon: newInputValue || '' } : null
                                    );
                                }}
                                renderOption={(props, option) => (
                                    <Box
                                        component="li"
                                        {...props}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                        <CatIcon category={option} />
                                        <span>{option}</span>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={LL.categoryForm.icon()}
                                        placeholder={LL.categoryForm.iconPlaceholder()}
                                        helperText={LL.categoryForm.iconHelperText()}
                                        required
                                        slotProps={{
                                            ...params.slotProps,

                                            input: {
                                                ...params.slotProps.input,
                                                startAdornment: editingCategory?.icon ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            ml: 1,
                                                        }}
                                                    >
                                                        <CatIcon category={editingCategory?.icon} />
                                                    </Box>
                                                ) : null,
                                            },
                                        }}
                                    />
                                )}
                            />
                            <Autocomplete
                                freeSolo
                                options={uniqueGroups}
                                value={editingCategory?.group || ''}
                                onChange={(_, newValue) => {
                                    setEditingCategory((prev) =>
                                        prev ? { ...prev, group: newValue || '' } : null
                                    );
                                }}
                                onInputChange={(_, newInputValue) => {
                                    setEditingCategory((prev) =>
                                        prev ? { ...prev, group: newInputValue || '' } : null
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={LL.categoryForm.group()}
                                        placeholder={LL.categoryForm.groupPlaceholder()}
                                        required
                                    />
                                )}
                            />
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label={LL.categoryForm.setupTime()}
                                    type="number"
                                    fullWidth
                                    value={editingCategory?.setupTime ?? 30}
                                    onChange={(e) =>
                                        setEditingCategory((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      setupTime: Math.max(
                                                          0,
                                                          parseInt(e.target.value) || 0
                                                      ),
                                                  }
                                                : null
                                        )
                                    }
                                    slotProps={{ htmlInput: { min: 0 } }}
                                    helperText={LL.categoryForm.setupTimeHelperText()}
                                />
                                <TextField
                                    label={LL.categoryForm.teardownTime()}
                                    type="number"
                                    fullWidth
                                    value={editingCategory?.teardownTime ?? 15}
                                    onChange={(e) =>
                                        setEditingCategory((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      teardownTime: Math.max(
                                                          0,
                                                          parseInt(e.target.value)
                                                      ),
                                                  }
                                                : null
                                        )
                                    }
                                    slotProps={{ htmlInput: { min: 0 } }}
                                    helperText={LL.categoryForm.teardownTimeHelperText()}
                                />
                            </Stack>
                            <TextField
                                label={LL.categoryForm.sortPriority()}
                                type="number"
                                fullWidth
                                value={editingCategory?.sortPriority ?? 500}
                                onChange={(e) =>
                                    setEditingCategory((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  sortPriority: Math.max(
                                                      0,
                                                      Math.min(999, parseInt(e.target.value) || 0)
                                                  ),
                                              }
                                            : null
                                    )
                                }
                                slotProps={{ htmlInput: { min: 0, max: 999 } }}
                                helperText={LL.categoryForm.sortPriorityHelperText()}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={(editingCategory?.allowMultiple ?? 0) === 1}
                                        onChange={(e) =>
                                            setEditingCategory((prev) => {
                                                if (!prev) return prev;
                                                const nextAllow = e.target.checked ? 1 : 0;
                                                const nextMin = nextAllow
                                                    ? Math.max(prev.minAssignments ?? 1, 1)
                                                    : 1;
                                                const nextMax = nextAllow
                                                    ? Math.max(prev.maxAssignments ?? 1, nextMin)
                                                    : 1;
                                                return {
                                                    ...prev,
                                                    allowMultiple: nextAllow,
                                                    minAssignments: nextMin,
                                                    maxAssignments: nextMax,
                                                };
                                            })
                                        }
                                    />
                                }
                                label={LL.adminCategoriesPage.allowMultipleAssignments()}
                            />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    label={LL.adminCategoriesPage.minAssignments()}
                                    type="number"
                                    value={editingCategory?.minAssignments ?? 1}
                                    disabled={(editingCategory?.allowMultiple ?? 0) !== 1}
                                    onChange={(e) => {
                                        const min = Math.max(
                                            parseInt(e.target.value || '1', 10),
                                            1
                                        );
                                        setEditingCategory((prev) => {
                                            if (!prev) return prev;
                                            return {
                                                ...prev,
                                                minAssignments: min,
                                                maxAssignments: Math.max(
                                                    prev.maxAssignments ?? min,
                                                    min
                                                ),
                                            };
                                        });
                                    }}
                                    fullWidth
                                    slotProps={{ htmlInput: { min: 1 } }}
                                />
                                <TextField
                                    label={LL.adminCategoriesPage.maxAssignments()}
                                    type="number"
                                    value={editingCategory?.maxAssignments ?? 1}
                                    disabled={(editingCategory?.allowMultiple ?? 0) !== 1}
                                    onChange={(e) => {
                                        setEditingCategory((prev) => {
                                            if (!prev) return prev;
                                            const min = prev.minAssignments ?? 1;
                                            const max = Math.max(
                                                parseInt(e.target.value || '1', 10),
                                                min
                                            );
                                            return {
                                                ...prev,
                                                maxAssignments: max,
                                            };
                                        });
                                    }}
                                    fullWidth
                                    slotProps={{
                                        htmlInput: { min: editingCategory?.minAssignments ?? 1 },
                                    }}
                                />
                            </Stack>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            (editingCategory?.notifyDaysBeforeWhenUnassigned ??
                                                null) !== null
                                        }
                                        onChange={(e) =>
                                            setEditingCategory((prev) => {
                                                if (!prev) return prev;
                                                return {
                                                    ...prev,
                                                    notifyDaysBeforeWhenUnassigned: e.target.checked
                                                        ? (prev.notifyDaysBeforeWhenUnassigned ?? 1)
                                                        : null,
                                                };
                                            })
                                        }
                                    />
                                }
                                label={
                                    LL.adminCategoriesPage.notifyUnfilledLabel
                                        ? LL.adminCategoriesPage.notifyUnfilledLabel()
                                        : 'Notify when unfilled'
                                }
                            />

                            {(editingCategory?.notifyDaysBeforeWhenUnassigned ?? null) !== null && (
                                <Stack direction="row" spacing={2} sx={{ px: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {LL.adminCategoriesPage.notifyDaysBeforeLabel({
                                            days:
                                                editingCategory?.notifyDaysBeforeWhenUnassigned ??
                                                1,
                                        })}
                                    </Typography>
                                    <Slider
                                        value={editingCategory?.notifyDaysBeforeWhenUnassigned ?? 3}
                                        onChange={(_, value) => {
                                            const raw = Array.isArray(value) ? value[0] : value;
                                            const v = Math.max(1, Math.min(10, raw));
                                            setEditingCategory((prev) =>
                                                prev
                                                    ? { ...prev, notifyDaysBeforeWhenUnassigned: v }
                                                    : prev
                                            );
                                        }}
                                        min={1}
                                        max={10}
                                        step={1}
                                        marks
                                        valueLabelDisplay="auto"
                                    />
                                </Stack>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseModal} startIcon={<CloseIcon />}>
                            {LL.common.cancel()}
                        </Button>
                        <Button
                            onClick={handleSaveCategory}
                            variant="contained"
                            startIcon={<SaveIcon />}
                            disabled={
                                !editingCategory?.name ||
                                !editingCategory?.icon ||
                                !editingCategory?.group
                            }
                        >
                            {editingCategory?.id ? LL.common.update() : LL.common.create()}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
};

export default AdminCategoriesPage;
